/**
 * CabinInterviewHub — /programs/kabin-memurlugu/interview
 * Ekran 4: Mulakat Simulasyonu (ogrenci) — prototip birebir layout
 *
 * Prototip ref: kariyer-online-dashboard.dc.html #isSim
 * Layout: max-w-[1080px] px-10 pt-[34px] pb-14
 * Sol setup karti + sag sidebar kartlari (CV Profilim, Hazirlik Raporum, Son Oturum)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Video, ChevronRight, Clock, ArrowLeft, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInterviewSimSessions } from '../hooks/cabin/useInterviewSimSessions';
import { useInterviewAvailability } from '../hooks/cabin/useInterviewAvailability';

const DIFFICULTY_LABELS_TR = ['Çok Kolay', 'Kolay', 'Orta', 'Zor', 'Çok Zor'];
const DIFFICULTY_LABELS_EN = ['Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'];

export default function CabinInterviewHub() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();

  const [difficulty, setDifficulty] = useState(3);
  const [language, setLanguage] = useState('tr');

  const { sessions: recentSessions, loading: sessionsLoading } = useInterviewSimSessions();
  const lastSession = recentSessions.length > 0 ? recentSessions[0] : null;

  const { data: availability, isLoading: availabilityLoading } = useInterviewAvailability();
  // Cooldown is active when endpoint has loaded and can_start is explicitly false
  const inCooldown = !availabilityLoading && availability?.can_start === false;

  const formattedNextDate = (() => {
    if (!availability?.next_available_at) return '';
    try {
      return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      }).format(new Date(availability.next_available_at));
    } catch { return ''; }
  })();

  const cooldownRelativeLabel = (() => {
    if (!inCooldown || availability?.remaining_seconds == null) return '';
    if (availability.remaining_seconds < 3600) {
      return t('interview.sim.cooldown.relative_today');
    }
    const days = Math.ceil(availability.remaining_seconds / 86400);
    return t('interview.sim.cooldown.relative').replace('{{days}}', days);
  })();

  const diffLabels = lang === 'tr' ? DIFFICULTY_LABELS_TR : DIFFICULTY_LABELS_EN;

  const handleStart = () => {
    navigate('/programs/kabin-memurlugu/interview/sim', { state: { difficulty, language } });
  };

  const lastSessionLangLabel = (() => {
    if (!lastSession) return '';
    const l = (lastSession.language || 'tr').toLowerCase();
    if (l === 'tr') return t('kariyer.lang.tr') || 'Türkçe';
    return t('kariyer.lang.en') || 'English';
  })();

  const lastSessionDate = (() => {
    if (!lastSession?.created_at) return '';
    try {
      return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }).format(new Date(lastSession.created_at));
    } catch { return ''; }
  })();

  return (
    <div style={{ padding: '34px 24px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
      {/* Responsive: dar viewport'ta (<700px) grid tek kolona düşer */}
      <style>{`
        @media (max-width: 699px) {
          .interview-hub-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* ── Baslik ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '26px' }}>
        {/* Geri butonu */}
        <button
          type="button"
          onClick={() => navigate('/programs/kabin-memurlugu/dashboard')}
          className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-xl border-2 border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.06)]"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('common.back')}
        </button>
        <div style={{
          fontSize: '12.5px', fontWeight: '800', letterSpacing: '1px',
          color: '#19C0CE', marginBottom: '7px',
        }}>
          {lang === 'tr' ? 'ÖĞRENCİ PANELİ' : 'STUDENT PANEL'}
        </div>
        <h1 style={{
          fontFamily: "'Fredoka','Nunito',sans-serif",
          fontWeight: '600', fontSize: '33px', margin: '0', color: '#13233F',
        }}>
          {t('interview.sim.title')}
        </h1>
        <p style={{ margin: '7px 0 0', color: '#6B7B92', fontSize: '15px' }}>
          {t('interview.sim.hub.subtitle')}
        </p>
      </div>

      {/* ── 2-kolon grid (sol: setup, sag: sidebar kartlar) ─────────── */}
      {/*
        Kök neden düzeltmesi: eski 1fr değerleri minmax olmadan dar viewport'ta
        grid sütunlarını sıkıştırıyordu. Sağ sütuna minimum 280px garantilendi.
        Büyük ekran: minmax(0,1.55fr) / minmax(280px,1fr)
        Küçük ekran (<700px): @media ile tek kolon (aşağıdaki style tag)
      */}
      <div
        className="interview-hub-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(280px, 1fr)',
          gap: '20px',
          alignItems: 'start',
        }}
      >

        {/* ── Sol: Setup karti ──────────────────────────────────────── */}
        <div style={{
          background: '#fff', border: '1px solid #E6ECF3',
          borderRadius: '22px', padding: '26px',
          boxShadow: '0 12px 30px rgba(16,40,73,.06)',
        }}>
          {/* Zorluk secici */}
          <div style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '1px', color: '#8593A6' }}>
            {lang === 'tr' ? 'ZORLUK' : 'DIFFICULTY'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginTop: '12px' }}>
            {[1, 2, 3, 4, 5].map((d) => {
              const isActive = difficulty === d;
              return (
                <motion.button
                  key={d}
                  type="button"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setDifficulty(d)}
                  aria-pressed={isActive}
                  aria-label={`${lang === 'tr' ? 'Zorluk' : 'Difficulty'} ${d}: ${diffLabels[d - 1]}`}
                  style={{
                    border: `1.5px solid ${isActive ? '#14B4CC' : '#E4EBF2'}`,
                    borderRadius: '13px', padding: '13px 0', textAlign: 'center',
                    cursor: 'pointer', background: isActive ? '#14B4CC' : '#fff',
                    boxShadow: isActive ? '0 8px 18px -6px rgba(20,180,204,.7)' : 'none',
                    transition: 'all .15s',
                  }}
                >
                  <div style={{
                    fontFamily: "'Fredoka','Nunito',sans-serif",
                    fontSize: '20px', fontWeight: '600',
                    color: isActive ? '#fff' : '#13233F',
                  }}>
                    {d}
                  </div>
                  <div style={{ fontSize: '11px', color: isActive ? '#D4F4F9' : '#9AA8BC', marginTop: '2px' }}>
                    {diffLabels[d - 1]}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Dil secici */}
          <div style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '1px', color: '#8593A6', marginTop: '24px' }}>
            {lang === 'tr' ? 'DİL' : 'LANGUAGE'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px' }}>
            {(['tr', 'en']).map((l) => {
              const isActive = language === l;
              const label = l === 'tr'
                ? (t('kariyer.lang.tr') || 'Türkçe')
                : (t('kariyer.lang.en') || 'English');
              return (
                <motion.button
                  key={l}
                  type="button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setLanguage(l)}
                  aria-pressed={isActive}
                  style={{
                    background: isActive ? '#14B4CC' : '#fff',
                    color: isActive ? '#fff' : '#13233F',
                    border: `1.5px solid ${isActive ? '#14B4CC' : '#E4EBF2'}`,
                    borderRadius: '13px', padding: '14px 0', textAlign: 'center',
                    fontFamily: 'inherit', fontWeight: '800', fontSize: '15px', cursor: 'pointer',
                    boxShadow: isActive ? '0 8px 18px -6px rgba(20,180,204,.7)' : 'none',
                    transition: 'all .15s',
                  }}
                >
                  {label}
                </motion.button>
              );
            })}
          </div>

          {/* Cooldown banner — only when can_start === false */}
          {inCooldown && (
            <div
              id="cooldown-status-banner"
              role="status"
              style={{
                marginTop: '24px',
                background: '#FFFBEB',
                border: '2px solid #FCD34D',
                borderRadius: '14px',
                padding: '14px 16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <Clock size={20} color="#D97706" aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: '800', fontSize: '14px', color: '#92400E', lineHeight: 1.3 }}>
                  {t('interview.sim.cooldown.banner_title')}
                </div>
                <div style={{ fontSize: '13px', color: '#B45309', marginTop: '4px' }}>
                  {t('interview.sim.cooldown.banner_sub').replace('{{date}}', formattedNextDate)}
                </div>
                {cooldownRelativeLabel ? (
                  <div style={{ fontSize: '12px', color: '#D97706', marginTop: '3px', fontWeight: '700' }}>
                    {cooldownRelativeLabel}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Baslat butonu */}
          <motion.button
            type="button"
            onClick={inCooldown ? undefined : handleStart}
            aria-disabled={inCooldown || undefined}
            tabIndex={inCooldown ? -1 : undefined}
            aria-describedby={inCooldown ? 'cooldown-status-banner' : undefined}
            whileHover={inCooldown ? {} : { scale: 1.02 }}
            whileTap={inCooldown ? {} : { scale: 0.98 }}
            style={{
              width: '100%',
              marginTop: inCooldown ? '10px' : '24px',
              border: 'none',
              borderRadius: '15px',
              background: inCooldown
                ? '#E4EBF2'
                : 'linear-gradient(135deg,#19C0CE,#1198B6)',
              color: inCooldown ? '#8593A6' : '#fff',
              fontFamily: "'Fredoka','Nunito',sans-serif",
              fontWeight: '600', fontSize: '18px', padding: '17px',
              cursor: inCooldown ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '11px',
              boxShadow: inCooldown ? 'none' : '0 14px 30px -10px rgba(20,180,204,.8)',
              transition: 'background .2s, color .2s, box-shadow .2s',
            }}
          >
            <Video size={22} aria-hidden="true" />
            {t('interview.sim.begin_btn')}
          </motion.button>

          {/* Caption shown only in cooldown */}
          {inCooldown && (
            <p style={{
              textAlign: 'center', fontSize: '12px', color: '#8593A6',
              marginTop: '6px', marginBottom: 0,
            }}>
              {t('interview.sim.cooldown.btn_caption')}
            </p>
          )}
        </div>

        {/* ── Sag: Sidebar kartlari ─────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* CV Profilim */}
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/programs/kabin-memurlugu/cv')}
            data-testid="interview-hub-cv-profile"
            style={{
              background: '#fff', border: '1px solid #E6ECF3', borderRadius: '18px',
              padding: '18px', boxShadow: '0 8px 22px rgba(16,40,73,.04)',
              display: 'flex', gap: '14px', alignItems: 'center', cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: '46px', height: '46px', flexShrink: 0, borderRadius: '13px',
              background: '#EEF0FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#5848C2"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '800', fontSize: '15px', color: '#13233F' }}>
                {t('cabin.interview.hub.cv_profile_title')}
              </div>
              <div style={{ fontSize: '12.5px', color: '#8593A6', marginTop: '2px', lineHeight: '1.4' }}>
                {t('cabin.interview.hub.cv_profile_desc')}
              </div>
            </div>
            <ChevronRight size={18} color="#B5C0CE" aria-hidden="true" />
          </motion.button>

          {/* Hazirlik Raporum */}
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/programs/kabin-memurlugu/interview/readiness-report')}
            data-testid="interview-hub-readiness-report"
            style={{
              background: '#fff', border: '1px solid #E6ECF3', borderRadius: '18px',
              padding: '18px', boxShadow: '0 8px 22px rgba(16,40,73,.04)',
              display: 'flex', gap: '14px', alignItems: 'center', cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: '46px', height: '46px', flexShrink: 0, borderRadius: '13px',
              background: '#E6F6EC',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#2FB85E"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3v18h18"/>
                <path d="m7 14 4-4 3 3 5-6"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '800', fontSize: '15px', color: '#13233F' }}>
                {t('cabin.interview.hub.readiness_title')}
              </div>
              <div style={{ fontSize: '12.5px', color: '#8593A6', marginTop: '2px', lineHeight: '1.4' }}>
                {t('cabin.interview.hub.readiness_desc')}
              </div>
            </div>
            <ChevronRight size={18} color="#B5C0CE" aria-hidden="true" />
          </motion.button>

          {/* Son oturum karti */}
          {sessionsLoading ? (
            /* Skeleton — beyaz, animate-pulse */
            <div
              className="bg-white rounded-2xl border-2 border-gray-200 animate-pulse"
              style={{ height: '140px' }}
              role="status"
              aria-label={t('common.loading') || 'Yükleniyor'}
            />
          ) : lastSession ? (() => {
            // Durum tek noktadan türetilsin (öncelik sırası):
            // 1) is_evaluated===true → SCORED
            // 2) is_evaluated!==true && status==='pending_review' → PENDING
            // 3) diğer → IN_PROGRESS
            const isEvaluated = lastSession.is_evaluated === true;
            const isPendingReview = !isEvaluated && lastSession.status === 'pending_review';

            // Rozet
            const badge = isEvaluated ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                {t('cabin.interview.session.status.scored')}
              </span>
            ) : isPendingReview ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                {t('cabin.interview.hub.badge.pending')}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                {t('cabin.interview.hub.badge.in_progress')}
              </span>
            );

            // Alt metin rozet ile çelişmesin
            const hintText = isEvaluated ? null
              : isPendingReview ? t('cabin.interview.hub.pending_hint')
              : t('cabin.interview.hub.in_progress_hint');

            // Skor satırı (yalnızca SCORED)
            const ccriTotal = lastSession.ccri_total;
            const result = lastSession.result;
            let scoreRow = null;
            if (isEvaluated) {
              if (ccriTotal != null) {
                scoreRow = (
                  <div style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontSize: '34px', fontWeight: '600', color: '#13233F', marginTop: '10px', lineHeight: 1 }}>
                    {Math.round(ccriTotal)}
                    <span style={{ fontSize: '16px', color: '#8593A6' }}>/100</span>
                  </div>
                );
              } else if (result) {
                const resultKey = `cabin.interview.hub.result.${result}`;
                const resultIcon = result === 'positive'
                  ? <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" />
                  : result === 'reserve'
                  ? <AlertCircle size={16} className="text-amber-600" aria-hidden="true" />
                  : <XCircle size={16} className="text-rose-600" aria-hidden="true" />;
                scoreRow = (
                  <div className="flex items-center gap-1.5 mt-2.5 text-sm font-bold text-gray-700">
                    {resultIcon}
                    {t(resultKey)}
                  </div>
                );
              }
            }

            return (
              <div
                className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5"
                role="region"
                aria-label={t('cabin.interview.hub.last_session')}
              >
                <div style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '1px', color: '#8593A6' }}>
                  {t('cabin.interview.hub.last_session')}
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {badge}
                  <span style={{ fontSize: '12.5px', color: '#8593A6' }}>
                    {t('cabin.interview.hub.difficulty_short')} {lastSession.difficulty} · {lastSessionLangLabel}
                  </span>
                </div>
                {hintText && (
                  <p style={{ fontSize: '12.5px', color: '#8593A6', marginTop: '6px' }}>
                    {hintText}
                  </p>
                )}
                {scoreRow}
                <div style={{ fontSize: '12px', color: '#B5C0CE', marginTop: '6px' }}>
                  {lastSessionDate}
                </div>
              </div>
            );
          })() : (
            /* Empty state */
            <div
              className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5 flex flex-col items-center justify-center text-center"
              role="status"
              style={{ minHeight: '120px' }}
            >
              <Video size={28} className="text-gray-300 mb-2" aria-hidden="true" />
              <p style={{ fontWeight: '700', fontSize: '14px', color: '#6B7B92' }}>
                {t('cabin.interview.hub.no_session_title')}
              </p>
              <p style={{ fontSize: '12px', color: '#9AA8BC', marginTop: '4px', lineHeight: 1.5 }}>
                {t('cabin.interview.hub.no_session_sub')}
              </p>
            </div>
          )}

          {/* Gecmis oturumlar linki */}
          {!sessionsLoading && recentSessions.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/programs/kabin-memurlugu/interview/history')}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '12px', borderRadius: '13px',
                border: '1.5px dashed #DCE4ED', background: 'transparent',
                fontSize: '13.5px', fontWeight: '700', color: '#8593A6',
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
              }}
            >
              <Clock size={15} aria-hidden="true" />
              {t('cabin.interview.hub.view_all')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
